import { createFileRoute, Link } from '@tanstack/react-router';
import { useConfirm } from '@/lib/alerts/hooks/useConfirm';
import { useAuth } from '@/lib/auth/hooks/useAuth';
import TextInput from '@/lib/common/inputs/TextInput';
import { getImageUrl } from '@/lib/constants';
import LoadingCard from '@/lib/loading/components/LoadingCard';
import { getAccessLabel, User, UserTeam } from '@/lib/models/user';
import useTeam from '@/lib/teams/hooks/useTeam';
import useAxios from '@/machinery/useAxios';
import { faArrowLeft, faDownload, faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { object, string } from 'yup';
import myAxios from '@/machinery/ax';
import { Button, Chip, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@heroui/react';
import UploadLogo from '@/components/account/teams/[id]/UploadLogo';

function UserRow({
    user,
    user_team,
    refetch,
    isAdmin,
}: {
    user: User;
    user_team: UserTeam;
    refetch: () => void;
    isAdmin: boolean;
}) {
    const { ax } = useAxios();
    const { showConfirm } = useConfirm();

    const handleDelete = useCallback(() => {
        showConfirm({
            title: 'Are you sure you want to delete this user?',
            onConfirmed: async () => {
                await ax({
                    method: 'delete',
                    url: `/teams/${user_team.team_id}/user/${user_team.user_id}`,
                });

                await refetch();
            },
        });
    }, [ax, refetch, showConfirm, user_team.team_id, user_team.user_id]);

    const canDeleteUser = useMemo(() => isAdmin && user_team.user_access !== 'admin', [isAdmin, user_team]);

    return (
        <div className='my-2 grid grid-cols-[1fr_auto] justify-between'>
            <div className='flex w-full flex-row items-center justify-between'>
                <div>
                    <div>{user.full_name}</div>
                    <div className='text-sm italic'>{user.email}</div>
                </div>
                <Chip color={user_team.user_access == 'admin' ? 'secondary' : 'default'}>
                    {getAccessLabel(user_team.user_access)}
                </Chip>
            </div>
            <Dropdown isDisabled={!canDeleteUser}>
                <DropdownTrigger disabled={!canDeleteUser}>
                    <Button isIconOnly variant='light' disabled={!canDeleteUser}>
                        <FontAwesomeIcon icon={faEllipsisV} />
                    </Button>
                </DropdownTrigger>
                <DropdownMenu>
                    <DropdownItem
                        key={'remove'}
                        color='danger'
                        onPress={handleDelete}
                        className='text-danger'>
                        Remove user
                    </DropdownItem>
                </DropdownMenu>
            </Dropdown>
        </div>
    );
}
type FormType = {
    email: string;
    first_name: string;
    last_name: string;
};

const schema = object().shape({
    email: string().email().required().label('Email'),
    first_name: string().required().label('First name'),
    last_name: string().required().label('Last name'),
});
export const Route = createFileRoute('/account/teams/$teamId')({
    component: RouteComponent,
});

function RouteComponent() {
    const { teamId } = Route.useParams();
    const { user } = useAuth();
    const { ax, loading } = useAxios();
    const [addUser, setAddUser] = useState(false);
    const [addLogo, setAddLogo] = useState(false);

    const { team, refetch, isLoading } = useTeam({ teamId: Number(teamId) });

    const isAdmin = useMemo(() => {
        return !!team?.user_teams?.some(
            userTeam => userTeam.user_id === user?.id && userTeam.user_access === 'admin',
        );
    }, [team?.user_teams, user?.id]);

    const f = useForm<FormType>({
        resolver: yupResolver(schema),
        mode: 'onChange',
    });

    const handleAddUser = useCallback(
        async (data: FormType) => {
            await await ax({
                method: 'post',
                url: `/teams/${teamId}/user`,
                data,
            });

            await refetch();
            setAddUser(false);
        },
        [ax, teamId, refetch],
    );

    const [downloading, setDownloading] = useState(false);

    const downloadPDF = useCallback(async () => {
        setDownloading(true);
        try {
            const response = await myAxios.get(`pdf/round_log/team/${teamId}`, {
                headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
                responseType: 'blob',
            });
            const href = URL.createObjectURL(response.data);

            // create "a" HTML element with href to file & click
            const link = document.createElement('a');
            link.href = href;
            link.target = '_blank';
            link.setAttribute('download', `CaddyStats_Logging_${team?.name}.pdf`); //or any other extension
            document.body.appendChild(link);
            link.click();

            // clean up "a" element & remove ObjectURL
            document.body.removeChild(link);
            URL.revokeObjectURL(href);
        } finally {
            setDownloading(false);
        }
    }, [teamId, team?.name]);

    return (
        <div className='mx-auto grid max-w-xl md:p-2'>
            <div className='grid w-full grid-cols-2 py-2 text-sm'>
                <Link to='/account/teams' className=''>
                    <Button
                        color='primary'
                        variant='light'
                        startContent={<FontAwesomeIcon icon={faArrowLeft} className='pr-2' />}>
                        Back to teams
                    </Button>
                </Link>
                <Button
                    color='primary'
                    variant='light'
                    onPress={downloadPDF}
                    isLoading={downloading}
                    startContent={<FontAwesomeIcon icon={faDownload} className='pr-2' />}>
                    Download Logging Template PDF
                </Button>
            </div>

            <div className='simple-card grid w-full gap-y-4 p-3 '>
                {isLoading || (!team && <LoadingCard />)}
                {team && (
                    <div className='grid'>
                        <div className='mb-4 flex flex-row items-center justify-between'>
                            <div className='flex flex-row items-center gap-2'>
                                {!!team.logo && (
                                    <img src={getImageUrl(team.logo)} alt='team-logo' className='h-8' />
                                )}
                                <div className='text-2xl font-bold'>{team.name}</div>
                            </div>
                            <Button color='primary' variant='light' onPress={() => setAddLogo(true)}>
                                {team.logo ? 'Change logo' : 'Add logo'}
                            </Button>
                            {addLogo && <UploadLogo team={team} setAddLogo={setAddLogo} />}
                        </div>
                        <div className='font-semibold'>Team Members</div>

                        {team?.users.map(user => {
                            const userTeam = team.user_teams.find(userTeam => userTeam.user_id === user.id);
                            return (
                                userTeam && (
                                    <UserRow
                                        key={user.id}
                                        user={user}
                                        user_team={userTeam}
                                        refetch={refetch}
                                        isAdmin={isAdmin}
                                    />
                                )
                            );
                        })}

                        {isAdmin && (
                            <>
                                {addUser && (
                                    <FormProvider {...f}>
                                        <form
                                            className='mt-3 grid gap-x-4 gap-y-2 border-t border-t-gray-500 pt-3'
                                            onSubmit={f.handleSubmit(handleAddUser)}>
                                            <div className='font-semibold'>Add New User</div>
                                            <TextInput
                                                name='email'
                                                label='Email'
                                                type='text'
                                                placeholder='email@example.com'
                                            />
                                            <TextInput
                                                name='first_name'
                                                label='First name'
                                                placeholder='John'
                                                type='text'
                                            />
                                            <TextInput
                                                name='last_name'
                                                label='Last name'
                                                placeholder='Doe'
                                                type='text'
                                            />
                                            <div className='grid-cols-2 justify-self-end'>
                                                <Button
                                                    color='primary'
                                                    variant='light'
                                                    className='btn-transparent text-gray-500'
                                                    onPress={() => setAddUser(false)}>
                                                    Cancel
                                                </Button>
                                                <Button
                                                    color='primary'
                                                    variant='solid'
                                                    type='submit'
                                                    disabled={loading}>
                                                    Add
                                                </Button>
                                            </div>
                                        </form>
                                    </FormProvider>
                                )}
                                {!addUser && (
                                    <Button variant='light' color='primary' onPress={() => setAddUser(true)}>
                                        Add user
                                    </Button>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
